import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { LoginContainerComponent } from '@connect-services/client/components/login-container/login-container.component';
import { PasswordRulesVerificationComponent } from '@connect-services/client/components/password-rules-verification/password-rules-verification.component';
import { VersionComponent } from '@connect-services/client/components/version/version.component';
import { AbstractSecurityComponent } from '@connect-services/client/pages/security/abstract-security/abstract-security.component';
import { CapitalizePipe } from '@connect-services/client/pipes/capitalize.pipe';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { LoginFormGroup, SecurityFormField } from '@shared/client/models/form.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CapitalizePipe,
        LoadingComponent,
        LoginContainerComponent,
        PasswordRulesVerificationComponent,
        RessourcesModule,
        VersionComponent,
    ],
    selector: 'app-login',
    styleUrl: '../abstract-security/abstract-security.component.scss',
    templateUrl: '../abstract-security/abstract-security.component.html',
})
export class LoginComponent extends AbstractSecurityComponent implements OnInit {
    public override actionLabel = 'connectMe';
    public override fieldNames = [SecurityFormField.IDENTIFIER, SecurityFormField.PASSWORD];
    public override readonly formGroup: WritableSignal<LoginFormGroup> = signal<LoginFormGroup>({} as LoginFormGroup);
    public override isLogin = true;
    public override title = 'loginToAccessYourOrganisation';

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);

    protected override initForm(): LoginFormGroup {
        return new LoginFormGroup({
            [SecurityFormField.IDENTIFIER]: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.email, Validators.required],
            }),
            [SecurityFormField.PASSWORD]: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.required],
            }),
        });
    }

    protected override async preInit(): Promise<void> {
        const hasSsoError = this.activatedRoute.snapshot.queryParams['ssoError'];
        if (hasSsoError) {
            return this.snackBarService.openErrorSnackBar('ssoAuthError');
        }

        const accessToken = this.activatedRoute.snapshot.queryParams['access_token'];
        if (accessToken) {
            this.isLoadingSSO.set(true);
            await this.securityService.setSession(accessToken);
            await this.router.navigate([''], { replaceUrl: true });

            return;
        }
    }

    protected override async submitAction(): Promise<void> {
        const formControls = this.formGroup().controls;
        await this.securityService.login(
            formControls[SecurityFormField.IDENTIFIER].value,
            formControls[SecurityFormField.PASSWORD].value,
        );
    }
}
